/**
 * Utilitaires du widget de don embarquable (embed cross-site).
 *
 * Contrat postMessage entre l'iframe (/embed/[slug], origine donoor.org) et le
 * site hôte qui l'embarque (via embed.js). Namespace `donoor-embed:` pour ne
 * jamais collisionner avec le canal `preview-*` du dashboard.
 *
 * Ces mêmes constantes/helpers sont réutilisés côté loader `public/embed.js`
 * (dupliqués en clair là-bas car ce fichier est du JS vanilla sans bundler) —
 * garder les DEUX synchronisés si on fait évoluer le protocole.
 */

/** Types de messages échangés sur le canal embed. */
export const EMBED_MESSAGE = {
  /** iframe → hôte : l'iframe est montée et prête. */
  READY: "donoor-embed:ready",
  /** iframe → hôte : nouvelle hauteur du contenu (auto-resize). */
  RESIZE: "donoor-embed:resize",
  /** iframe → hôte : paiement terminé (déclenche window.Donoor.onDonation). */
  PAYMENT_COMPLETED: "donoor-embed:payment_completed",
  /** hôte → iframe : couleurs héritées du site hôte (thème auto). */
  THEME: "donoor-embed:theme",
  /** hôte → iframe : pré-remplissage du formulaire. */
  PREFILL: "donoor-embed:prefill",
  /** hôte → iframe : mise à jour LIVE de la config d'apparence (aperçu). */
  CONFIG: "donoor-embed:config",
} as const;

export type EmbedMessageType =
  (typeof EMBED_MESSAGE)[keyof typeof EMBED_MESSAGE];

/**
 * Valide qu'une chaîne est une origine HTTP(S) bien formée (protocole + host,
 * sans chemin). Sert à valider l'`?host=` transmis par embed.js AVANT de
 * l'utiliser comme cible de postMessage. On refuse tout ce qui n'est pas une
 * origine http(s) stricte (jamais `*`, jamais un chemin).
 */
export function isValidHostOrigin(raw: unknown): raw is string {
  if (typeof raw !== "string" || !raw) return false;
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    // Une origine = protocole + host (+ port). Pas de chemin/query/hash.
    return url.origin === raw || `${url.origin}/` === `${raw}/`;
  } catch {
    return false;
  }
}

/**
 * SÉCURITÉ — sanitize une valeur de couleur poussée par l'hôte avant de
 * l'injecter dans du CSS inline. N'accepte que des formes strictes (hex 3/6,
 * rgb/rgba, ou nom CSS simple) pour éviter toute injection CSS via le canal
 * `donoor-embed:theme`. Renvoie null si invalide.
 */
export function sanitizeCssColor(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const v = raw.trim();
  if (!v || v.length > 32) return null;
  // Hex #rgb / #rrggbb (avec alpha optionnel).
  if (/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v)) return v;
  // rgb()/rgba() avec uniquement chiffres, virgules, points, espaces, %.
  if (/^rgba?\(\s*[\d.,%\s]+\)$/.test(v)) return v;
  // Nom de couleur CSS simple (lettres uniquement, ex. "teal").
  if (/^[a-zA-Z]{3,20}$/.test(v)) return v;
  return null;
}

/**
 * Paramètres du widget (`embed`, `host`, `primary`) à REPORTER sur la page
 * suivante du tunnel (paiement → succès). Sans eux, la page de succès
 * s'affichait en page complète (bannière, barre, pied de page) DANS l'iframe
 * de l'association, et le cadre ne suivait plus sa hauteur (Karim
 * 2026-09-29). Vide hors widget. `host` et `primary` sont revalidés : on ne
 * recopie jamais une valeur que `useEmbedFrame` refuserait.
 */
export function parametresIntegration(query: Record<string, unknown>): Record<string, string> {
  if (query.embed !== "1" && query.embed !== "true") return {};
  const params: Record<string, string> = { embed: "1" };
  if (isValidHostOrigin(query.host)) params.host = query.host;
  const primary = sanitizeCssColor(query.primary);
  if (primary) params.primary = primary;
  return params;
}

/** Idem, à coller derrière une URL qui a déjà des paramètres (`&embed=1…`, ou rien). */
export function suiteIntegration(query: Record<string, unknown>): string {
  const q = new URLSearchParams(parametresIntegration(query)).toString();
  return q ? `&${q}` : "";
}
